import {useEffect, useState} from 'react'

import {warnServerEnvironment} from '../../utils/logging/server-warning'
import {getQueryStringObject} from '../../utils/url'
import {
  getThemeOverrideFromCookie,
  persistThemeOverrideOnCookie
} from '../business/persistData'
import {shouldAllowOverrides} from '../utils/overrides'

/**
 * Hook for managing theme override functionality in non-production environments and demo sites.
 *
 * - If `themeOverride` is in the query params, it takes precedence and gets persisted to a cookie
 * - If no query parameter is provided, falls back to the `themeOverride` cookie, if exists
 * - Always returns `null` in production environments (unless on demo sites)
 * @param environment - The current application environment ('production', 'development', 'staging', etc.)
 * @returns The active theme override string, or `null` if none is set or not allowed
 * @example
 * ```tsx
 * const themeOverride = useThemeOverride(process.env.NEXT_PUBLIC_ENV)
 *
 * // URL: /page?themeOverride=ovago
 * // Returns: 'ovago' (and persists to cookie)
 *
 * // URL: /page (with existing cookie)
 * // Returns: 'ovago' (from cookie)
 *
 * // Production environment (non-demo site)
 * // URL: /page?themeOverride=ovago
 * // Returns: null (regardless of URL or cookie)
 *
 * // Demo site in production
 * // URL: https://demo.vio.com?themeOverride=ovago
 * // Returns: 'ovago' (works on demo sites even in production)
 * ```
 */
export const useThemeOverride = (environment: string) => {
  const [themeOverride, setThemeOverride] = useState<string | null>(null)

  useEffect(() => {
    // Server-side safety check
    if (typeof window === 'undefined') {
      warnServerEnvironment('useThemeOverride')
      setThemeOverride(null)
      return
    }

    // Check if overrides are allowed in this environment
    const {hostname} = location
    if (!shouldAllowOverrides(environment, hostname)) {
      setThemeOverride(null)
      return
    }

    const queryThemeOverride = getQueryStringObject()?.themeOverride as string
    const cookieThemeOverride = getThemeOverrideFromCookie()

    // Use query param and persist to cookie
    if (queryThemeOverride) {
      setThemeOverride(queryThemeOverride)
      persistThemeOverrideOnCookie(queryThemeOverride)
      return
    }

    // Use cookie if no query param is provided
    if (cookieThemeOverride) {
      setThemeOverride(cookieThemeOverride)
      return
    }

    // No valid override found
    setThemeOverride(null)
  }, [environment])

  return themeOverride
}
